import { Link } from 'react-router-dom';
import {
  BUSINESS_HOURS_FOOTER,
  BUSINESS_NAME,
  PUBLIC_PHONE,
  LEGAL_ENTITY,
  NO_PUBLIC_LOCATION,
  SERVICE_TERRITORY
} from '@lib/businessContact';

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gradient-to-br from-[#1E1B4B] to-[#0F0F23] border-t border-[#7C3AED]/30" role="contentinfo">
      {/* Main Footer Content */}
      <div className="relative pt-16 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Top Section with Logo and Description */}
          <div className="mb-12 lg:mb-16">
            <div className="flex flex-col items-center text-center max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold mb-4">
                <span className="text-[#7C3AED]">A</span>
                <span className="text-white">vision</span>
                <span className="text-white/80"> Lighting</span>
              </h2>
                <p className="text-white/80 text-lg mb-2">
                  Commercial lighting in{' '}
                  <span className="text-[#06B6D4] font-semibold">{SERVICE_TERRITORY.primaryLabel}</span>
                  {' '}— {SERVICE_TERRITORY.primaryCounties.join(', ')}.
                </p>
                <p className="text-white/55 text-sm mb-2">
                  {SERVICE_TERRITORY.nationwideLabel}: qualifying large-scale commercial and specialty installs.
                </p>
            </div>
          </div>

          {/* Grid Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Quick Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-3">
                {[
                  { to: '/about', label: 'About' },
                  { to: '/services', label: 'Services' },
                  { to: '/projects', label: 'Projects' },
                  { to: '/guides', label: 'Guides' },
                  { to: '/service-area', label: 'Service Area' },
                  { to: '/contact', label: 'Request an assessment' }
                ].map((link) => (
                  <li key={link.label}>
                    <Link 
                      to={link.to}
                      className="text-white/80 hover:text-[#06B6D4] transition-colors flex items-center group"
                    >
                      <i className="fas fa-chevron-right text-xs mr-2 text-[#7C3AED] group-hover:translate-x-1 transition-transform"></i>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-white font-semibold mb-4">Our Services</h3>
              <ul className="space-y-3">
                {[
                  { to: '/services/entertainment-rgbw-dmx', label: 'RGBW & DMX lighting' },
                  { to: '/services/commercial-led', label: 'Commercial LED retrofits' },
                  { to: '/services/warehouse-lighting', label: 'Warehouse lighting' },
                  { to: '/services/sports-lighting', label: 'Sports lighting' },
                  { to: '/services/recreation-lighting', label: 'Recreation lighting' },
                  { to: '/services/architectural-lighting', label: 'Architectural lighting' },
                  { to: '/services/lighting-controls', label: 'Lighting controls' }
                ].map((service) => (
                  <li key={service.to}>
                    <Link 
                      to={service.to}
                      className="text-white/80 hover:text-[#EC4899] transition-colors flex items-center group"
                    >
                      <i className="fas fa-check text-xs mr-2 text-[#06B6D4]"></i>
                      {service.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-white font-semibold mb-4">Contact Info</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-white/90 text-sm font-medium mb-2">Text Us (Fastest Response):</p>
                  <a
                    href={PUBLIC_PHONE.sms}
                    className="flex items-center text-white/80 hover:text-[#06B6D4] transition-colors group"
                  >
                    <i className="fas fa-comment-dots mr-2 text-[#EC4899] group-hover:scale-110 transition-transform"></i>
                    {PUBLIC_PHONE.display}
                  </a>
                  <a
                    href={PUBLIC_PHONE.tel}
                    className="flex items-center text-white/80 hover:text-[#06B6D4] transition-colors mt-2"
                  >
                    <i className="fas fa-phone mr-2 text-[#06B6D4]"></i>
                    Call {PUBLIC_PHONE.display}
                  </a>
                </div>
                <div>
                  <p className="text-white/90 text-sm font-medium mb-2">Business Hours:</p>
                  <p className="text-white/80">
                    {BUSINESS_HOURS_FOOTER}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Service Coverage</h3>
              <p className="text-white/90 text-sm mb-2">{LEGAL_ENTITY.legalName}</p>
              <p className="text-white/80 mb-3">
                <i className="fas fa-map-marker-alt text-[#EC4899] mr-2"></i>
                {LEGAL_ENTITY.locality}, {LEGAL_ENTITY.region}
              </p>
              <p className="text-white/70 text-sm mb-3">
                {SERVICE_TERRITORY.primaryCounties.join(', ')}. {NO_PUBLIC_LOCATION}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 py-6 bg-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-white/60 text-sm text-center md:text-left">
              &copy; {currentYear} {BUSINESS_NAME}. {LEGAL_ENTITY.legalName}.
            </p>
            <div className="flex items-center space-x-6">
              <Link 
                to="/contact"
                className="text-white/60 hover:text-[#06B6D4] transition-colors text-sm"
              >
                Contact Us
              </Link>
              <Link 
                to="/services"
                className="text-white/60 hover:text-[#06B6D4] transition-colors text-sm"
              >
                Our Services
              </Link>
              <Link 
                to="/about"
                className="text-white/60 hover:text-[#06B6D4] transition-colors text-sm"
              >
                About
              </Link>
              <Link
                to="/privacy"
                className="text-white/60 hover:text-[#06B6D4] transition-colors text-sm"
              >
                Privacy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
